import { html, unsafeCSS } from 'lit';
import { query, customElement } from 'lit/decorators.js';
import { styles as specificAccordionStyles } from '../view/view.scss';
import { WEB_COMPONENT_POLICY_PANEL_ANTIMALWARE_ON_DEMAND } from '../../../../../../../webcomponents.constants';
import { translateFn } from '../../../../../../../utils/translator.helper';
import { GzFormFieldWebComponent } from '../../../../../../../ui/gzForm/formField';
import { GzToggleWebComponent } from '../../../../../../../ui/gzToggle';
import { FormGroup } from '../../../../../../../ui/gzForm/formGroup/form-group';
import { GzPolicyPanelAccordionTemplateWebComponent } from '../../../../../../common/policyPanel/uiSettings/accordionTemplate';
import _ from 'lodash';
import { FormGroupValue } from '../../../../../../../ui/gzForm/formGroup/types/form-group-value';
import { PolicyPanelManager } from '../../../../PolicyPanelManager';
import deepmerge from 'deepmerge';
import { toggleDisabled } from '../../../../../../../utils/htmlElement.utils';
import { USE_EXT_JS_VERSION } from '../../../../config/config';
import { FontSizes, FontWeights, LabelWidthValues } from '../../../../../../../types/common/types';
import { ButtonTypes } from '../../../../../../../ui/gzButton/types';
import {
  FullPagePopupConfigs,
  GzPolicyFullPageWindow,
} from '../../../../../../common/policyPanel/uiSettings/dialogs/fullPage/GzPolicyFullPageWindow';
import { ValidationResult } from '../../../../../../../ui/gzForm/formField/types/form-validator';
import { GzHardCodedFormField } from '../../../../../../../ui/gzForm/formField/component/GzHardCodedFormField';
import { GzAntimalwareOnDemandScanTasksGridWebComponent } from '../gridComponents/scanTasks/GzAntimalwareOnDemandScanTasksGridWebComponent';
import {
  AntimalwareOnDemandFormValue,
  AntimalwareOnDemandScanTaskFormValue,
  AntimalwareOnDemandSearchableKeywords,
} from '../../../../utils/uiSettings/antimalware/antimalware.types';
import {
  ANTIMALWARE_ON_DEMAND_DEFAULT_VALUE,
  getAntimalwareOnDemandScanningProfileTypeOptions,
  getAntimalwareOnDemandSearchableKeywords,
} from '../../../../utils/uiSettings/antimalware/antimalware.values';
import {
  AntimalwareOnDemandContextualScan,
  AntimalwareOnDemandDeviceScanSettings,
  AntimalwareOnDemandScanTask,
  AntimalwareOnDemandScanTaskScanType,
  ModuleId,
  SectionId,
} from '@nirvana/shared/dtos/types/policies/common';
import { GzAntimalwareOnDemandContextualScanFormWebComponent } from '../forms/contextualScanForm';
import { GzAntimalwareOnDemandDeviceScanFormWebComponent } from '../forms/deviceScanForm';
import { GzCheckboxWebComponent } from '../../../../../../../ui/gzCheckbox';
import { GzInputNumberWebComponent, InputWidthValues } from '../../../../../../../ui/gzInput';
import { GzSingleSelectWebComponent } from '../../../../../../../ui/gzSingleSelectDropdown';
import { MinValidator } from '../../../../../../../ui/gzForm/validators/min.validator';

@customElement(WEB_COMPONENT_POLICY_PANEL_ANTIMALWARE_ON_DEMAND)
export class GzAntimalwareOnDemandWebComponent extends GzPolicyPanelAccordionTemplateWebComponent<
  AntimalwareOnDemandFormValue,
  AntimalwareOnDemandSearchableKeywords
> {
  protected path = {
    moduleId: ModuleId.ANTIMALWARE,
    sectionId: SectionId.ANTIMALWARE_ON_DEMAND,
  };
  protected searchableKeywords = getAntimalwareOnDemandSearchableKeywords();

  // this field is used to store the initial value of the antimalware contextual scan settings
  private contextualScanField: GzHardCodedFormField<AntimalwareOnDemandContextualScan | undefined> =
    new GzHardCodedFormField<AntimalwareOnDemandContextualScan | undefined>(undefined);

  // this field is used to store the initial value of the antimalware device scan settings
  private deviceScanField: GzHardCodedFormField<AntimalwareOnDemandDeviceScanSettings | undefined> =
    new GzHardCodedFormField<AntimalwareOnDemandDeviceScanSettings | undefined>(undefined);

  @query('#antimalware-on-demand-scan-tasks-field')
  private onDemandScanTasksField: GzFormFieldWebComponent<
    GzAntimalwareOnDemandScanTasksGridWebComponent,
    AntimalwareOnDemandScanTask[]
  >;

  @query('#antimalware-on-demand-device-scan-settings-contextual-scan-type')
  private onDemandContextualScanType: GzSingleSelectWebComponent;
  @query('#antimalware-on-demand-device-scan-settings-device-scan-type')
  private onDemandDeviceScanType: GzSingleSelectWebComponent;

  @query('#antimalware-on-demand-device-scanning-content')
  private onDemandDeviceScanningContent: HTMLElement;
  @query('#antimalware-on-demand-device-scanning-enable-field')
  private onDemandDeviceScanningEnableField: GzFormFieldWebComponent<GzToggleWebComponent, boolean>;
  @query('#antimalware-on-demand-device-scanning-settings-cd-field')
  private onDemandDeviceScanningSettingsCdField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
  @query('#antimalware-on-demand-device-scanning-settings-usb-field')
  private onDemandDeviceScanningSettingsUsbField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
  @query('#antimalware-on-demand-device-scanning-settings-enable-field')
  private onDemandDeviceScanningSettingsEnableField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
  @query('#antimalware-on-demand-device-scanning-settings-maxim-field')
  private onDemandDeviceScanningSettingsMaximField: GzFormFieldWebComponent<GzInputNumberWebComponent, number>;

  /**
   * Current accordion styles merged with template styles.
   */
  static get styles() {
    return [...GzPolicyPanelAccordionTemplateWebComponent.styles, unsafeCSS(`${specificAccordionStyles}`)];
  }

  /**
   * Render specific title.
   */
  protected renderTitle() {
    return html` <gz-title
      class="content"
      searchable-label="${this.searchableKeywords[AntimalwareOnDemandSearchableKeywords.OnDemand].label}"
      ><span>${this.searchableKeywords[AntimalwareOnDemandSearchableKeywords.OnDemand].translatedLabel}</span>
    </gz-title>`;
  }

  /**
   * Render specific info text.
   * @protected
   */
  protected renderInfoText() {
    return html`
      <gz-text
        id="antimalware-on-demand-text"
        fontSize="${FontSizes.PARAGRAPH_12}"
        fontWeight="${FontWeights.NORMAL}"
        class="infoText"
        >${translateFn('On-demand Scanning inspects endpoints for threats based on scheduled scan tasks.')}
      </gz-text>
    `;
  }

  /**
   * Render specific content.
   */
  protected renderContent() {
    return html`
      <!-- antimalware on demand scan tasks -->
      <div id="antimalware-on-demand-scan-tasks-content">
        <gz-title
          fontWeight="${FontWeights.MEDIUM}"
          fontSize="${FontSizes.BODY}"
          class="mt-4 fit-content"
          searchable-label="${this.searchableKeywords[AntimalwareOnDemandSearchableKeywords.ScanTasks].label}"
          >${this.searchableKeywords[AntimalwareOnDemandSearchableKeywords.ScanTasks].translatedLabel}</gz-title
        >

        <gz-form-field id="antimalware-on-demand-scan-tasks-field" status-field>
          <gz-policy-panel-antimalware-on-demand-scan-tasks-grid
            class="removeGridPadding"
            gz-form-input
          ></gz-policy-panel-antimalware-on-demand-scan-tasks-grid>
        </gz-form-field>
      </div>

      <!-- antimalware on demand scan settings -->
      <div id="antimalware-on-demand-scan-settings-content">
        <gz-title
          fontWeight="${FontWeights.MEDIUM}"
          fontSize="${FontSizes.BODY}"
          class="mt-4 fit-content"
          searchable-label="${this.searchableKeywords[AntimalwareOnDemandSearchableKeywords.ScanSettings].label}"
          ><span>${this.searchableKeywords[AntimalwareOnDemandSearchableKeywords.ScanSettings].translatedLabel}</span>
        </gz-title>

        <div id="antimalware-on-demand-scan-settings-contextual-scan-section">
          <gz-title
            fontWeight="${FontWeights.SEMIBOLD}"
            fontSize="${FontSizes.PARAGRAPH_12}"
            class="mt-2 subtitle"
            searchable-label="${this.searchableKeywords[AntimalwareOnDemandSearchableKeywords.ContextualScan].label}"
            >${this.searchableKeywords[AntimalwareOnDemandSearchableKeywords.ContextualScan].translatedLabel}</gz-title
          >

          <gz-text fontSize="${FontSizes.PARAGRAPH_12}" fontWeight="${FontWeights.NORMAL}" class="infoText mt-1"
            >${translateFn(
              'Configure the settings for scanning files and folders on the local endpoint from Windows Explorer contextual menu. You can choose a predefined scan profile or customize the contextual scanning settings, such as the type of threats to scan for or the action to be taken on each detection type.'
            )}
          </gz-text>

          <div class="field-with-button">
            <gz-single-select
              id="antimalware-on-demand-device-scan-settings-contextual-scan-type"
              label="${translateFn('Scan options')}"
              labelWidth="${LabelWidthValues.LARGE}"
              .filterOptions=${getAntimalwareOnDemandScanningProfileTypeOptions()}
              readonly
            ></gz-single-select>

            <gz-button
              id="antimalware-on-demand-device-scan-settings-contextual-edit-button"
              type="${ButtonTypes.OUTLINE}"
              @click="${this.onEditContextualScanSettingsButtonClick.bind(this)}"
              class="mt-2"
              >${translateFn('Edit')}</gz-button
            >
          </div>
        </div>

        <div id="antimalware-on-demand-scan-settings-device-scan-section">
          <gz-title
            fontWeight="${FontWeights.SEMIBOLD}"
            fontSize="${FontSizes.PARAGRAPH_12}"
            class="mt-4 subtitle"
            searchable-label="${this.searchableKeywords[AntimalwareOnDemandSearchableKeywords.ExternalDevicesScan]
              .label}"
            >${this.searchableKeywords[AntimalwareOnDemandSearchableKeywords.ExternalDevicesScan]
              .translatedLabel}</gz-title
          >

          <gz-text fontSize="${FontSizes.PARAGRAPH_12}" fontWeight="${FontWeights.NORMAL}" class="infoText mt-1"
            >${translateFn(
              'Configure the scans options for the specified external devices. You can choose a predefined scan profile or customize the device scanning settings, such as the file types to scan or the action to be taken on each detection type.'
            )}
          </gz-text>

          <div class="field-with-button">
            <gz-single-select
              id="antimalware-on-demand-device-scan-settings-device-scan-type"
              label="${translateFn('Scan options')}"
              labelWidth="${LabelWidthValues.LARGE}"
              .filterOptions=${getAntimalwareOnDemandScanningProfileTypeOptions()}
              readonly
            ></gz-single-select>

            <gz-button
              id="antimalware-on-demand-device-scan-settings-device-edit-button"
              type="${ButtonTypes.OUTLINE}"
              @click="${this.onEditDeviceScanSettingsButtonClick.bind(this)}"
              class="mt-2"
              >${translateFn('Edit')}</gz-button
            >
          </div>
        </div>
      </div>

      <div>
        <gz-form-field id="antimalware-on-demand-device-scanning-enable-field" class="mt-4 mbw-1">
          <gz-toggle
            class="content-title-toggle"
            searchable-label="${this.searchableKeywords[AntimalwareOnDemandSearchableKeywords.DeviceScanning].label}"
            gz-form-input
            @toggleChange="${this.onDeviceScanningToggleChange}"
          >
            <span
              >${this.searchableKeywords[AntimalwareOnDemandSearchableKeywords.DeviceScanning].translatedLabel}</span
            >
          </gz-toggle>
        </gz-form-field>

        <div id="antimalware-on-demand-device-scanning-content">
          <gz-text fontSize="${FontSizes.PARAGRAPH_12}" fontWeight="${FontWeights.NORMAL}" class="infoText mb-2"
            >${translateFn(
              'Antimalware automatically detects and scans external storage devices when they are connected to endpoints. No action can be taken on infected objects detected on CDs, DVDs, or on mapped network drives that allow read-only access. Disabling device scanning does not affect endpoint protection if On-access Scanning is enabled.'
            )}
          </gz-text>

          <gz-form-field id="antimalware-on-demand-device-scanning-settings-cd-field">
            <gz-checkbox
              searchable-label="${this.searchableKeywords[AntimalwareOnDemandSearchableKeywords.CdDvdMedia].label}"
              label="${this.searchableKeywords[AntimalwareOnDemandSearchableKeywords.CdDvdMedia].translatedLabel}"
              gz-form-input
              @change="${this.checkDeviceScanningScanCdStatus}"
            ></gz-checkbox>
          </gz-form-field>

          <gz-form-field id="antimalware-on-demand-device-scanning-settings-usb-field">
            <gz-checkbox
              searchable-label="${this.searchableKeywords[AntimalwareOnDemandSearchableKeywords.UsbStorageDevices]
                .label}"
              label="${this.searchableKeywords[AntimalwareOnDemandSearchableKeywords.UsbStorageDevices]
                .translatedLabel}"
              gz-form-input
              @change="${this.checkDeviceScanningScanUsbStatus}"
            ></gz-checkbox>
          </gz-form-field>

          <div class="checkbox-with-input">
            <gz-form-field class="checkbox-width-450" id="antimalware-on-demand-device-scanning-settings-enable-field">
              <gz-checkbox
                searchable-label="${this.searchableKeywords[AntimalwareOnDemandSearchableKeywords.DoNotScan].label}"
                label="${this.searchableKeywords[AntimalwareOnDemandSearchableKeywords.DoNotScan].translatedLabel +
                ':'}"
                gz-form-input
                @change="${this.checkDeviceScanningScanDeviceStatus}"
              ></gz-checkbox>
            </gz-form-field>

            <gz-form-field id="antimalware-on-demand-device-scanning-settings-maxim-field" class="mr-2 disabled">
              <gz-input-number
                gz-form-input
                .max=${999999999}
                unitSuffix="${translateFn('MB')}"
                inputWidth=${InputWidthValues.MEDIUM}
              ></gz-input-number>
            </gz-form-field>
          </div>
        </div>
      </div>
    `;
  }

  private onDeviceScanningToggleChange(event: CustomEvent) {
    const value = event.detail.checked;
    toggleDisabled(this.onDemandDeviceScanningContent, !value);

    // keep values displayed in the UI
    if (!this.isReady || !value) {
      return;
    }

    // check if at least one scanning option is enabled
    const scanCd = this.onDemandDeviceScanningSettingsCdField.value;
    const scanUsb = this.onDemandDeviceScanningSettingsUsbField.value;
    const scanDevices = this.onDemandDeviceScanningSettingsEnableField.value;
    if (scanCd || scanUsb || scanDevices) {
      return;
    }

    // if all are disabled, enable CD/DVD media & USB scanning
    this.onDemandDeviceScanningSettingsCdField.value = value;
    this.onDemandDeviceScanningSettingsUsbField.value = value;
  }

  private checkDeviceScanningScanCdStatus() {
    // wait for component to be ready
    if (!this.isReady) {
      return;
    }

    this.checkDeviceScanningStatus();
  }

  private checkDeviceScanningScanUsbStatus() {
    // wait for component to be ready
    if (!this.isReady) {
      return;
    }

    this.checkDeviceScanningStatus();
  }

  private checkDeviceScanningScanDeviceStatus(e: CustomEvent) {
    const disabled = !e.detail.checked;
    toggleDisabled(this.onDemandDeviceScanningSettingsMaximField, disabled);
    this.onDemandDeviceScanningSettingsMaximField.Validators = disabled
      ? []
      : [
          MinValidator(
            1,
            translateFn('Enter a number greater than {minValue}.', {
              minValue: 1,
            })
          ),
        ];
    this.onDemandDeviceScanningSettingsMaximField.validate(true);

    // wait for component to be ready
    if (!this.isReady) {
      return;
    }

    this.checkDeviceScanningStatus();
  }

  checkDeviceScanningStatus() {
    const scanCd = this.onDemandDeviceScanningSettingsCdField.value;
    const scanUsb = this.onDemandDeviceScanningSettingsUsbField.value;
    const scanDevices = this.onDemandDeviceScanningSettingsEnableField.value;

    if (scanCd || scanUsb || scanDevices) {
      return;
    }

    if (!this.onDemandDeviceScanningEnableField.value) {
      return;
    }

    this.onDemandDeviceScanningEnableField.value = false;
  }

  private onEditContextualScanSettingsButtonClick(): void {
    if (!this.valueT) {
      return;
    }

    const bodyForm = new GzAntimalwareOnDemandContextualScanFormWebComponent(
      AntimalwareOnDemandScanTaskScanType.CONTEXTUAL,
      this.contextualScanField.value
    );
    const confirmCallback = (formValue: AntimalwareOnDemandContextualScan): ValidationResult => {
      this.contextualScanField.value = formValue;
      this.onDemandContextualScanType.value = formValue.scanProfile;
      return { valid: true };
    };
    const resetCallback = (): void => bodyForm.resetValue();

    // create configs
    const configs: FullPagePopupConfigs = {
      bodyForm: bodyForm,
      strings: {
        title: translateFn('Contextual scan task'),
        confirm: translateFn('Save#preserve state'),
        reset: translateFn('Reset to default'),
        cancel: translateFn('Cancel'),
        cancelConfirmation: {
          title: translateFn('Discard changes?'),
          body: translateFn('Changes you have made will be lost.'),
          cancel: translateFn('Keep changes'),
          confirm: translateFn('Discard'),
        },
      },
      confirmCallbackFn: confirmCallback,
      resetCallbackFn: resetCallback,
      shouldShowCancelConfirmation: true,
      shouldShowResetButton: true,
    };
    // show window
    const window = new GzPolicyFullPageWindow(configs);
    window.show();
  }

  private onEditDeviceScanSettingsButtonClick(): void {
    if (!this.valueT) {
      return;
    }

    const bodyForm = new GzAntimalwareOnDemandDeviceScanFormWebComponent(
      AntimalwareOnDemandScanTaskScanType.DEVICE,
      this.deviceScanField.value
    );
    const confirmCallback = (formValue: AntimalwareOnDemandScanTaskFormValue): ValidationResult => {
      this.deviceScanField.value = formValue;
      this.onDemandDeviceScanType.value = formValue.scanProfile;
      return { valid: true };
    };
    const resetCallback = (): void => bodyForm.resetValue();

    // create configs
    const configs: FullPagePopupConfigs = {
      bodyForm: bodyForm,
      strings: {
        title: translateFn('External device scan task'),
        confirm: translateFn('Save#preserve state'),
        reset: translateFn('Reset to default'),
        cancel: translateFn('Cancel'),
        cancelConfirmation: {
          title: translateFn('Discard changes?'),
          body: translateFn('Changes you have made will be lost.'),
          cancel: translateFn('Keep changes'),
          confirm: translateFn('Discard'),
        },
      },
      confirmCallbackFn: confirmCallback,
      resetCallbackFn: resetCallback,
      shouldShowCancelConfirmation: true,
      shouldShowResetButton: true,
    };
    // show window
    const window = new GzPolicyFullPageWindow(configs);
    window.show();
  }

  /**
   * Get data from manager and set missing fields.
   * Fields used in this section:
   * - uiSettings.antimalware.onDemand
   */
  protected getValueFromManager(): AntimalwareOnDemandFormValue {
    // get default value
    const defaultValue = {
      uiSettings: {
        [ModuleId.ANTIMALWARE]: { [SectionId.ANTIMALWARE_ON_DEMAND]: ANTIMALWARE_ON_DEMAND_DEFAULT_VALUE },
      },
    };

    // get value from manager
    const value = {
      uiSettings: {
        [ModuleId.ANTIMALWARE]: {
          [SectionId.ANTIMALWARE_ON_DEMAND]: PolicyPanelManager.getInstance().getPolicy(
            'uiSettings.antimalware.onDemand'
          ),
        },
      },
    };

    // merge received value with default one
    return deepmerge(defaultValue, value);
  }

  /**
   * Create form group.
   */
  protected createFormGroup() {
    this.formGroup = FormGroup.build({
      uiSettings: {
        antimalware: {
          onDemand: {
            scanTask: this.onDemandScanTasksField,
            contextualScan: this.contextualScanField,
            deviceScanning: {
              enable: this.onDemandDeviceScanningEnableField,
              settings: {
                automaticallyScanCdDvd: this.onDemandDeviceScanningSettingsCdField,
                automaticallyScanUsb: this.onDemandDeviceScanningSettingsUsbField,
                scanDevices: {
                  enable: this.onDemandDeviceScanningSettingsEnableField,
                  maxim: this.onDemandDeviceScanningSettingsMaximField,
                },
              },
              formSettings: this.deviceScanField,
            },
          },
        },
      },
    });
  }

  /**
   * Set form group value.
   */
  protected setFormData() {
    if (!this.valueT) {
      return;
    }

    // set form group value
    this.formGroup.value = {
      uiSettings: {
        antimalware: {
          onDemand: {
            scanTask: this.valueT.uiSettings.antimalware.onDemand.scanTask,
            contextualScan: this.valueT.uiSettings.antimalware.onDemand.contextualScan,
            deviceScanning: {
              enable: this.valueT.uiSettings.antimalware.onDemand.deviceScanning.enable,
              settings: this.valueT.uiSettings.antimalware.onDemand.deviceScanning.settings,
              formSettings: {
                scanProfile: this.valueT.uiSettings.antimalware.onDemand.deviceScanning.scanProfile,
                scanSettings: this.valueT.uiSettings.antimalware.onDemand.deviceScanning.scanSettings,
              },
            },
          },
        },
      },
    };

    this.onDemandContextualScanType.value = this.valueT.uiSettings.antimalware.onDemand.contextualScan.scanProfile;
    this.onDemandDeviceScanType.value = this.valueT.uiSettings.antimalware.onDemand.deviceScanning.scanProfile;
  }

  /**
   * Get form group value according to backend format.
   */
  protected getFormGroupValue(): FormGroupValue | null {
    if (!this.formGroup) {
      return null;
    }

    const value = _.cloneDeep(this.formGroup.value);

    // set device settings
    const deviceSettings = _.get(
      value,
      ['uiSettings', 'antimalware', 'onDemand', 'deviceScanning', 'formSettings'],
      {}
    );
    _.merge(value, { uiSettings: { antimalware: { onDemand: { deviceScanning: deviceSettings } } } });
    _.unset(value, ['uiSettings', 'antimalware', 'onDemand', 'deviceScanning', 'formSettings']);

    if (USE_EXT_JS_VERSION) {
      return _.get(value, ['uiSettings', 'antimalware', 'onDemand'], {});
    }

    return value;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    WEB_COMPONENT_POLICY_PANEL_ANTIMALWARE_ON_DEMAND: GzAntimalwareOnDemandWebComponent;
  }
}
